// Всплывающая панель под кнопкой (components/Dropdown/README.md) для выбора, которому мало
// списка: период «с — по» с быстрыми пилюлями. Esc и клик снаружи закрывают, фокус
// возвращается на кнопку — если страница не перевела его сама, пока панель закрывалась
// (переход из ленты уведомлений на строку заказа).
//
// 011: панель можно прикрепить к элементу страницы без кнопки-триггера — пометке в ячейке
// таблицы протокола, которую рисует не React (anchor). Тогда фокус после закрытия возвращает
// вызывающий.
import * as RadixPopover from '@radix-ui/react-popover';
import { useMemo, useRef } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { cx } from '../lib/cx.ts';
import { focusMovedOn } from '../lib/return-focus.ts';

export interface PopoverProps {
  /** Кнопка, которая открывает панель; должна принимать ref и обработчики. */
  trigger?: ReactElement;
  /** Элемент, к которому прикреплена панель, если кнопки-триггера нет. */
  anchor?: HTMLElement | null;
  /** Имя панели для программ чтения экрана. */
  label: string;
  children: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  align?: 'start' | 'center' | 'end';
  className?: string;
  /**
   * Фокус при открытии — на саму панель, а не на первый элемент: если у первого элемента есть
   * подсказка, она не всплывает сама и первый Esc закрывает панель (приёмка 013, «Столбцы»).
   */
  focusPanel?: boolean;
}

export function Popover({
  trigger,
  anchor,
  label,
  children,
  open,
  onOpenChange,
  align = 'start',
  className,
  focusPanel = false,
}: PopoverProps) {
  const virtualRef = useMemo(() => ({ current: anchor ?? null }), [anchor]);
  const panelRef = useRef<HTMLDivElement>(null);
  return (
    <RadixPopover.Root open={open} onOpenChange={onOpenChange}>
      {trigger ? <RadixPopover.Trigger asChild>{trigger}</RadixPopover.Trigger> : null}
      {anchor ? <RadixPopover.Anchor virtualRef={virtualRef} /> : null}
      <RadixPopover.Portal>
        <RadixPopover.Content
          ref={panelRef}
          aria-label={label}
          align={align}
          sideOffset={4}
          collisionPadding={8}
          className={cx('t-pop kp-pop kp-popover', className)}
          {...(focusPanel
            ? {
                tabIndex: -1,
                onOpenAutoFocus: (event: Event) => {
                  event.preventDefault();
                  panelRef.current?.focus();
                },
              }
            : {})}
          onCloseAutoFocus={(event) => {
            // Панель уже вынута из страницы: фокус в ней быть не может. Без кнопки-триггера
            // фокус возвращает вызывающий.
            if (!trigger || focusMovedOn()) event.preventDefault();
          }}
        >
          {children}
        </RadixPopover.Content>
      </RadixPopover.Portal>
    </RadixPopover.Root>
  );
}
